import { motion } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Trophy, Flame, Target, Star } from 'lucide-react';
import CosmeticsMenu from './CosmeticsMenu';
import { PlayerCosmetics, getCosmeticById } from '@/lib/cosmetics';

interface PlayerStatsProps {
  level: number;
  totalXP: number;
  levelProgress: number;
  streak: number;
  totalGames: number;
  totalWins: number;
  cosmetics: PlayerCosmetics;
  onCosmeticsChange: (cosmetics: PlayerCosmetics) => void;
}

export default function PlayerStats({
  level,
  totalXP,
  levelProgress,
  streak,
  totalGames,
  totalWins,
  cosmetics,
  onCosmeticsChange,
}: PlayerStatsProps) {
  const winRate = totalGames > 0 ? Math.round((totalWins / totalGames) * 100) : 0;
  const badge = getCosmeticById(cosmetics.badge);
  const title = getCosmeticById(cosmetics.title);
  const avatarColor = getCosmeticById(cosmetics.avatarColor);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
    >
      <Card className="glass-card p-4 space-y-4">
        {/* Profile Header */}
        <div className="flex items-center gap-4">
          <div 
            className="w-14 h-14 rounded-full border-4 border-border/50 flex items-center justify-center text-2xl shrink-0"
            style={{ background: avatarColor?.value || 'hsl(160 84% 39%)' }}
          >
            {badge?.value || '🌱'}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-black text-lg">Level {level}</span>
              <span className="text-xs text-primary font-bold">{title?.value || 'Player'}</span>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <Progress value={levelProgress} className="h-2 flex-1" />
              <span className="text-xs text-muted-foreground shrink-0">{Math.round(levelProgress)}%</span>
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {totalXP.toLocaleString()} XP total
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="p-2 bg-secondary/30 rounded-lg">
            <div className="flex items-center justify-center gap-1 text-orange-500">
              <Flame className="h-4 w-4" />
              <span className="font-black text-lg">{streak}</span>
            </div>
            <div className="text-xs text-muted-foreground">Streak</div>
          </div>
          
          <div className="p-2 bg-secondary/30 rounded-lg">
            <div className="flex items-center justify-center gap-1 text-primary">
              <Trophy className="h-4 w-4" />
              <span className="font-black text-lg">{totalWins}</span>
            </div>
            <div className="text-xs text-muted-foreground">Wins</div>
          </div>
          
          <div className="p-2 bg-secondary/30 rounded-lg">
            <div className="flex items-center justify-center gap-1 text-yellow-500">
              <Star className="h-4 w-4" />
              <span className="font-black text-lg">{winRate}%</span>
            </div>
            <div className="text-xs text-muted-foreground">Win Rate</div>
          </div>
        </div>

        {/* Customize Button */}
        <CosmeticsMenu
          level={level}
          selectedCosmetics={cosmetics}
          onSelect={onCosmeticsChange}
        />
      </Card>
    </motion.div>
  );
}